package org.example.project

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.padding
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import org.w3c.dom.events.Event
import kotlinx.browser.document
import org.example.project.firebase.FirestoreManager
import org.example.project.model.GameState
import org.example.project.ui.*
import org.example.project.utils.AudioManager
import org.example.project.viewmodel.AuthViewModel
import org.example.project.viewmodel.GameViewModel
import org.example.project.viewmodel.UserViewModel

@Composable
fun App() {
    val viewModel = remember { GameViewModel() }
    val authModel = remember { AuthViewModel() }
    val userViewModel = remember { UserViewModel() }

    LaunchedEffect(authModel.authReady, authModel.loggedIn) {
        if (!authModel.authReady) return@LaunchedEffect
        if (authModel.loggedIn) {
            userViewModel.loadUser()
        } else {
            userViewModel.clearUser()
        }
    }

    when {
        !authModel.authReady -> LoadingScreen()

        !authModel.loggedIn -> AuthScreen(authViewModel = authModel)

        else -> AuthenticatedApp(
            viewModel = viewModel,
            authModel = authModel,
            userViewModel = userViewModel
        )
    }
}

@Composable
private fun AuthenticatedApp(
    viewModel: GameViewModel,
    authModel: AuthViewModel,
    userViewModel: UserViewModel
) {
    LaunchedEffect(authModel.authReady, authModel.loggedIn) {
        if (!authModel.authReady)
            return@LaunchedEffect

    }
    when (viewModel.gameState) {
        GameState.HOME -> {
            when {
                userViewModel.isLoading -> LoadingScreen()
                userViewModel.currentUser != null -> HomeScreen(
                    user = userViewModel.currentUser!!,
                    onFindMatch = {
                        AudioManager.startMusicOnFirstInteraction()
                        viewModel.startMatchmaking(userViewModel.currentUser!!)
                    },
                    onLeaderboard = {
                        viewModel.navigateTo(GameState.LEADERBOARD)
                    },
                    onLogout = {

                        userViewModel.clearUser()
                        authModel.logout()
                    },
                    rank = userViewModel.rank,
                    viewModel = viewModel
                )
                else -> ProfileErrorScreen(
                    message = userViewModel.loadError ?: "Could not load your profile.",
                    onRetry = { userViewModel.loadUser() },
                    onLogout = {
                        userViewModel.clearUser()
                        authModel.logout()
                    }
                )
            }
        }

        GameState.MATCHMAKING -> {
            MatchmakingScreen(
                opponentName = viewModel.opponentName,
                error = viewModel.matchmakingError,
                onCancel = { viewModel.cancelMatchmaking() }
            )
        }

        GameState.SHIP_PLACEMENT -> {
            ShipPlacementScreen(
                viewModel = viewModel,
                opponentName = viewModel.opponentName,
                opponentReady = viewModel.multiplayerGame?.let { game ->
                    if (game.isPlayer1(viewModel.myUid)) game.player2Ready else game.player1Ready
                } ?: false
            )
        }

        GameState.COUNTDOWN -> {
            CountdownScreen(
                countdownStart = viewModel.multiplayerGame?.countdownStart ?: 0.0,
                onFinished = { viewModel.onCountdownFinished() }
            )
        }

        GameState.BATTLE -> {
            BattleShipScreen(viewModel)
        }

        GameState.RESULT -> {
            if (viewModel.didIWin()) AudioManager.playVictory()
            else AudioManager.playDefeat()
            ResultScreen(
                playerName = viewModel.playerName,
                score = viewModel.localScore,
                penalties = viewModel.localPenalties,
                correctAnswers = viewModel.localCorrect,
                won = viewModel.didIWin(),
                opponentName = viewModel.opponentName,
                onPlayAgain = { userViewModel.currentUser?.let {
                    viewModel.startNewMatch(it)
                } },
                onHome = {
                    viewModel.resetToHome()
                    userViewModel.loadUser()
                }
            )
        }

        GameState.LEADERBOARD -> {
            LeaderboardScreen(
                onBack = { viewModel.resetToHome() }
            )
        }

        else -> {}
    }
}

@Composable
fun LoadingScreen() {
    Box(
        modifier = Modifier.fillMaxSize(),
        contentAlignment = Alignment.Center
    ) {
        CircularProgressIndicator()
    }
}

@Composable
private fun ProfileErrorScreen(
    message: String,
    onRetry: () -> Unit,
    onLogout: () -> Unit
) {
    Box(
        modifier = Modifier.fillMaxSize(),
        contentAlignment = Alignment.Center
    ) {
        Column(
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.spacedBy(16.dp),
            modifier = Modifier.padding(24.dp)
        ) {
            Text(
                text = message,
                color = Color.White,
                fontWeight = FontWeight.Bold
            )
            Button(onClick = onRetry) {
                Text("Retry")
            }
            Button(onClick = onLogout) {
                Text("Log Out")
            }
        }
    }
}
